//#region Imports
import { booleanAttribute, Component, computed, HostListener, input, Input, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { HeaderItem } from './models/header';
import { PageService } from '../../services/page/page.service';
import { MeService } from '../../services/me/me.service';
import { InitialCircleComponent } from '../initial-circle/initial-circle.component';
import { IconComponent } from '../icon/icon.component';
import { ModalAction } from '../../modals/modal-options/modal-options.component';
import { HeaderEnum, PermissionEnum } from '../../../../environments/shared-environment/permission';
import { PermissionService } from '../../services/permission/permission.service';
import { AuthService } from '../../services/auth/auth.service';

//#endregion

@Component({
  selector: 'l-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    CommonModule,
    RouterLink,
    NgTemplateOutlet,
    InitialCircleComponent,
    IconComponent
  ],
})
export class HeaderComponent {

  //#region Constructor

  constructor(
    private readonly router: Router,
    private readonly pageService: PageService,
    private readonly meService: MeService,
    private readonly permissionService: PermissionService,
    private readonly authService: AuthService
  ) {}

  //#endregion

  //#region Public Properties

  @Input({ required: true })
  public headerItens: HeaderItem[] = [];

  public foreground = input(false, { transform: booleanAttribute });

  public activatedIndex = computed((): number => {
    const currentPageKey = this.pageService.page();
    if (!currentPageKey) return -1;

    return this.headerItens.findIndex(item => item.key === currentPageKey);
  });

  public username = signal(this.meService.getMe());

  public isOpen: boolean = false;

  public screenWidth!: number;

  public isLargeScreen: boolean = false;

  public modalActions: ModalAction[] = [
    {
      label: 'Minhas comparações',
      icon: 'text_compare',
      callback: async () => {
        await this.navigateTo('main/documents/comparison/my-comparisons');
      },
      colorClass: 'text-lPrimary'
    },
    {
      label: 'Sair',
      icon: 'logout',
      callback: async () => {
        await this.logout();
      },
      colorClass: 'text-red-600'
    }
  ];

  @HostListener('window:resize', [])
  onResize() {
    this.screenWidth = window.innerWidth;
    this.isLargeScreen = this.screenWidth >= 1024;
  }

  //#endregion

  //#region Public Methods

  public ngOnInit(): void {
    this.onResize();

    const can = (resource: HeaderEnum, permission: PermissionEnum, menu: ModalAction | ModalAction[]): ModalAction[] => this.permissionService.has(resource, permission) ? (Array.isArray(menu) ? menu : [menu]) : [];

    this.modalActions = [
      ...can(HeaderEnum.MANUAL_ANALYSIS, PermissionEnum.CREATE,
        {
          label: 'Minhas análises',
          icon: 'analytics',
          callback: async () => {
            await this.navigateTo('main/manual-analysis/my-analysis');
          },
          colorClass: 'text-lPrimary'
        }
      ),
      ...this.modalActions
    ];
  }

  public async navigateTo(url: string): Promise<void> {
    await this.router.navigate([url]);
  }

  public toggleHeader(index: number): void {
    const item = this.headerItens[index];
    if (!item) return;

    this.headerItens[index].active = !this.headerItens[index].active;
    this.isOpen = false;
  }

  public handleAction(action: ModalAction, event: Event): void {
    action.callback?.(event);
    this.isOpen = false;
  }

  public toggleHamburgerMenu(): void {
    this.isOpen = !this.isOpen;
  }

  //#endregion

  //#region Private Methods

  public async logout(): Promise<void> {
    await this.authService.logout();
    await this.router.navigate(['/login']);
  }

  //#endregion
  
}
